import { Alert, Button, Card, Empty, Space, Spin, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader, Grid as ZhGrid } from "zhihao-ui-react";

import { LOG_DATE_FORMAT } from "@/config";
import { dateFormat } from "@/utils";
import { SHIP_SAIL_STATUS } from "@/meta/logistics/shipInfo";
import { loadFindShipDetail } from "./data";
import type { FindShipDetail, FindShipDetailLoader } from "./types";

export interface FindLocationDetailPageProps {
    loadShip?: FindShipDetailLoader;
}

function display(value: unknown): string {
    return value === undefined || value === null || value === "" ? "-" : String(value);
}

/** 旧 Vue 使用船舶导航枚举展示状态，未知值仍保留原始值便于排查数据。 */
function shipStatus(value?: string | number) {
    return SHIP_SAIL_STATUS.getIntroByCode(value) || display(value);
}

/** 旧 Vue 使用 ct 毫秒时间戳展示最近一次 AIS 更新时间。 */
function updatedAt(value?: string | number) {
    if (value === undefined || value === null || value === "") return "--";
    return dateFormat(new Date(Number(value)), LOG_DATE_FORMAT);
}

/** 查船详情页；对应 Vue 通过 mmsi 调用 find-create 并展示当前定位。 */
export function FindLocationDetailPage({ loadShip = loadFindShipDetail }: FindLocationDetailPageProps) {
    const { mmsi = "" } = useParams();
    const location = useLocation();
    const routeMmsi = mmsi || location.pathname.match(/\/findLocation\/([^/]+)$/)?.[1] || "";
    const navigate = useNavigate();
    const [detail, setDetail] = useState<FindShipDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (!routeMmsi) {
            setErrorMessage("缺少船舶 MMSI");
            return;
        }
        let disposed = false;
        setLoading(true);
        void loadShip(routeMmsi)
            .then(result => {
                if (disposed) return;
                setDetail(result);
                setErrorMessage("");
            })
            .catch(() => {
                if (!disposed) setErrorMessage("船舶详情加载失败");
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [loadShip, routeMmsi]);

    return (
        <section aria-label="find-location-detail-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Space align="center" wrap>
                    <Typography.Title className="mb-0" level={4}>
                        船舶位置
                    </Typography.Title>
                    <Tag>{routeMmsi || "-"}</Tag>
                </Space>
            </Card>
            <Card>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {loading ? <Spin aria-label="船舶详情加载中" /> : null}
                {!loading && !errorMessage && !detail ? <Empty description="暂无船舶数据" /> : null}
                {detail ? (
                    <Space className="w-full" direction="vertical" size="large">
                        <ZhDetailHeader
                            leftInfos={[{ label: "MMSI", value: display(detail.id || routeMmsi) }]}
                            stateDesc={display(detail.status)}
                            title={display(detail.cnname)}
                        />
                        <ZhGrid cols={2} gap={[30, 20]}>
                            <ZhBaseItem label="MMSI" value={display(detail.id || routeMmsi)} />
                            <ZhBaseItem label="英文船名" value={display(detail.name)} />
                            <ZhBaseItem label="状态" value={shipStatus(detail.status)} />
                            <ZhBaseItem label="位置" value={<>{display(detail.location)}</>} />
                            <ZhBaseItem label="预到时间" value={display(detail.eta)} />
                            <ZhBaseItem
                                label="当前位置"
                                value={
                                    detail.lon !== undefined && detail.lat !== undefined ? (
                                        <span>{`${display(detail.lon)}, ${display(detail.lat)}`}</span>
                                    ) : (
                                        "-"
                                    )
                                }
                            />
                            <ZhBaseItem label="目的地" value={display(detail.dest)} />
                            <ZhBaseItem label="航速" value={display(detail.spd)} />
                            <ZhBaseItem label="航迹向" value={display(detail.cog)} />
                            <ZhBaseItem label="船艏向" value={display(detail.hdg)} />
                        </ZhGrid>
                        <Typography.Text>船位追踪：更新于{updatedAt(detail.ct)}</Typography.Text>
                        <Space>
                            <Button
                                aria-label="查询轨迹"
                                type="primary"
                                onClick={() => navigate(`/logistics/findTrack/${encodeURIComponent(routeMmsi)}/latest`)}
                            >
                                查询轨迹
                            </Button>
                            <Button aria-label="返回查船" onClick={() => navigate("/logistics/findLocation")}>
                                返回查船
                            </Button>
                        </Space>
                    </Space>
                ) : null}
            </Card>
        </section>
    );
}

export default FindLocationDetailPage;
